import { StockItem } from '../../../../../../common/types/StockItemTypes';
import { VideoSelection } from './videoUtils';

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && !!window?.sessionStorage;

export const STORAGE_KEY = 'videoEditorModalState';
export const STATE_EXPIRY_TIME = 3600000; // 1 hour in milliseconds

export interface SerializedVideoEditorState {
  stockItem: StockItem;
  selection: VideoSelection | null;
  selectedUseCase: string | null;
  defaultPlaceholder: string;
  promptText: string;
  timestamp: number;
}

export interface AutoOpenState {
  shouldOpen: boolean;
  storedState: SerializedVideoEditorState | null;
}

export const serializeModalState = (
  state: SerializedVideoEditorState
): void => {
  if (!isBrowser()) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save video editor state to sessionStorage:', error);
  }
};

export const deserializeModalState = (): SerializedVideoEditorState | null => {
  if (!isBrowser()) return null;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const state: SerializedVideoEditorState = JSON.parse(stored);

    // Check if state is fresh (less than 1 hour old)
    if (Date.now() - state.timestamp > STATE_EXPIRY_TIME) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return state;
  } catch (error) {
    console.warn(
      'Failed to restore video editor state from sessionStorage:',
      error
    );
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const clearModalSessionStorageState = (): void => {
  if (!isBrowser()) return;

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn(
      'Failed to clear video editor state from sessionStorage:',
      error
    );
  }
};

export const getAutoOpenState = (): AutoOpenState => {
  if (!isBrowser()) {
    return { shouldOpen: false, storedState: null };
  }

  const urlParams = new URLSearchParams(window.location.search);
  const hasVideoEditParam = urlParams.get('videoEditModal') === 'true';

  if (!hasVideoEditParam) {
    return { shouldOpen: false, storedState: null };
  }

  const state = deserializeModalState();
  return {
    shouldOpen: hasVideoEditParam && state !== null,
    storedState: state,
  };
};

export const buildModalRestoreUrl = (
  pathname?: string,
  search?: string
): string => {
  const currentPathname =
    pathname ?? (isBrowser() ? window.location.pathname : '');
  const currentSearch = search ?? (isBrowser() ? window.location.search : '');

  const hasExistingParams = currentSearch.length > 0;
  const separator = hasExistingParams ? '&' : '?';

  return `${currentPathname}${currentSearch}${separator}videoEditModal=true`;
};

export const removeVideoEditModalParam = (): void => {
  if (!isBrowser()) return;

  try {
    const urlParams = new URLSearchParams(window.location.search);

    // Check if the param exists before attempting to remove
    if (!urlParams.has('videoEditModal')) return;

    // Remove the param
    urlParams.delete('videoEditModal');

    // Build the new URL
    const newSearch = urlParams.toString();
    const newUrl = `${window.location.pathname}${
      newSearch ? `?${newSearch}` : ''
    }`;

    // Update the URL without creating a new history entry
    window.history.replaceState({}, '', newUrl);
  } catch (error) {
    console.warn('Failed to remove videoEditModal param from URL:', error);
  }
};

// --- Polling state persistence (survives page navigation) ---

export const POLLING_STATE_KEY = 'videoEditorPollingState';

export interface PollingState {
  lastPromptText?: string;
  lastSelection?: { startTime: number; duration: number };
  timestamp: number;
}

export const savePollingState = (
  state: Omit<PollingState, 'timestamp'>
): void => {
  if (!isBrowser()) return;

  try {
    const payload: PollingState = { ...state, timestamp: Date.now() };
    sessionStorage.setItem(POLLING_STATE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to save polling state to sessionStorage:', error);
  }
};

export const getPollingState = (): PollingState | null => {
  if (!isBrowser()) return null;

  try {
    const stored = sessionStorage.getItem(POLLING_STATE_KEY);
    if (!stored) return null;

    const state: PollingState = JSON.parse(stored);

    if (Date.now() - state.timestamp > STATE_EXPIRY_TIME) {
      sessionStorage.removeItem(POLLING_STATE_KEY);
      return null;
    }

    return state;
  } catch (error) {
    console.warn('Failed to restore polling state from sessionStorage:', error);
    sessionStorage.removeItem(POLLING_STATE_KEY);
    return null;
  }
};

export const clearPollingState = (): void => {
  if (!isBrowser()) return;

  try {
    sessionStorage.removeItem(POLLING_STATE_KEY);
  } catch (error) {
    console.warn('Failed to clear polling state from sessionStorage:', error);
  }
};
